<script setup lang="ts">
import { AppSwiperGallery } from '@libs/ui';

import {
  useCoreStore,
  useStatsigEventsStore,
} from '../../../../../core/stores';
import type {
  ActivityProductCardData,
  ActivitySegmentContext,
} from '../../../models';
import ImgProductCardLayout from '../ImgProductCardLayout.vue';
import ProductCardFooter from '../ProductCardFooter.vue';
import DateCardTag from '../DateCardTag.vue';

import ActivityProductCardContent from './ActivityProductCardContent.vue';

interface ActivityProductCardProps {
  date: string;
  productCardData: ActivityProductCardData;
  productCardContext: ActivitySegmentContext;
  printMode: boolean;
}

const props = defineProps<ActivityProductCardProps>();

interface ActivityProductCardEmits {
  (event: 'show-details'): void;
  (event: 'show-details-tickets'): void;
  (event: 'change-product'): void;
  (event: 'remove'): void;
}

const emit = defineEmits<ActivityProductCardEmits>();

const coreStore = useCoreStore();
const statsigEventsStore = useStatsigEventsStore();

function onGalleryImageSwipe(): void {
  statsigEventsStore.logTripOverviewActivityGalleryImageSwipe();
}
</script>

<template>
  <div class="activity-product-card">
    <ImgProductCardLayout :enable-hover-color="!props.printMode">
      <template #image>
        <AppSwiperGallery
          :image-urls="props.productCardData.imgSrcs"
          :show-nav-buttons="!props.printMode"
          :show-number-of-slides="!props.printMode"
          :display-gallery-dots="false"
          :allow-full-gallery="false"
          :error-image-handler="
            (src) =>
              coreStore.handleImageLoadError({
                src: src,
                source: 'activity-product-card-swiper-gallery',
              })
          "
          :print-mode="props.printMode"
          :full-gallery-aria-label="$t('a11y.open_image_gallery')"
          :left-arrow-aria-label="$t('a11y.previous_image')"
          :right-arrow-aria-label="$t('a11y.next_image')"
          @click="emit('show-details')"
          @gallery-image-swipe="onGalleryImageSwipe"
        />
      </template>
      <template #content>
        <ActivityProductCardContent
          :product-card-content="props.productCardData.productCardContent"
          :product-card-context="props.productCardContext"
          @show-details="emit('show-details')"
        />
      </template>
      <template #footer>
        <ProductCardFooter @show-details="emit('show-details')" />
      </template>
      <template #tag>
        <DateCardTag :date="props.date" />
      </template>
    </ImgProductCardLayout>
  </div>
</template>

<style scoped lang="scss">
.activity-product-card-content {
  display: flex;
  flex-direction: column;
  height: 100%;

  .button-wrapper {
    display: flex;
    justify-content: flex-end;
    padding-top: var(--spacing-12);
    align-items: center;
    gap: var(--spacing-16);
    @include for-less-or-equal-than-599-px {
      padding: var(--spacing-12) var(--spacing-16) 0 var(--spacing-16);
    }
    .details {
      display: flex;
      @include font-label-m-light;
      text-decoration: underline;
      cursor: pointer;
    }
  }
}
</style>
